import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Play, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Award } from 'lucide-react';
import { theme } from '../theme';
import { SectionCard, StatCard, StatusPill } from '../components/swipe';

// Mock NABH Audit — runs ~17 evidence checks that mirror what an actual
// assessor would verify on visit-day. Returns a weighted readiness score
// + per-chapter breakdown + a "top 5 failures" list to focus remediation.
export default function MockAuditPage({ API_BASE }) {
  const [questions, setQuestions] = useState([]);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const loadQuestions = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/nabh/mock-audit/questions`);
      setQuestions(r.data || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
  }, [API_BASE]);
  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  const runAudit = async () => {
    setRunning(true); setError('');
    try {
      const r = await axios.post(`${API_BASE}/nabh/mock-audit/run`);
      setReport(r.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setRunning(false);
  };

  const readinessLabel = report ? ({
    'audit-ready': { tone: 'success', label: '✓ Audit-Ready',  desc: '85%+ — schedule the assessor visit' },
    'on-track':    { tone: 'warning', label: '↗ On Track',    desc: '65–84% — fixable in 2-4 weeks' },
    'needs-work':  { tone: 'danger',  label: '⚠ Needs Work',  desc: '<65% — significant remediation needed' },
  }[report.readiness] || {}) : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, color: theme.navy, fontSize: '1.4rem', fontWeight: 700 }}>Mock NABH Audit</h2>
          <p style={{ margin: '2px 0 0', color: theme.textMuted, fontSize: '0.86rem' }}>
            {questions.length} live evidence checks against your current data — a 60-second dress rehearsal for the real assessor visit
          </p>
        </div>
        <button onClick={runAudit} disabled={running} style={primaryBtn}>
          {running ? <RefreshCw size={14} className="spin"/> : <Play size={14}/>}
          {running ? 'Running…' : 'Run Mock Audit'}
        </button>
      </div>

      {error && <div style={{ padding: 10, marginBottom: 14, backgroundColor: '#fef2f2', color: theme.danger, borderRadius: 8 }}>{error}</div>}

      {/* Question catalog (shown before any run) */}
      {!report && (
        <SectionCard variant="white" title="What we check" subtitle="Click 'Run Mock Audit' to evaluate each item against live data">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 8 }}>
            {questions.map(q => (
              <div key={q.id} style={{ padding: 12, backgroundColor: theme.bg, borderRadius: 8, fontSize: '0.84rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <code style={{ fontSize: '0.74rem', color: theme.primary, fontWeight: 700 }}>{q.id}</code>
                  <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>weight {q.weight}</span>
                </div>
                <div style={{ color: theme.navy }}>{q.question}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Report */}
      {report && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
            <StatCard
              label="Readiness Score"
              value={`${report.overall}%`}
              tone={readinessLabel.tone}
              sub={readinessLabel.label}
              icon={<Award size={22}/>}
            />
            <StatCard label="Checks Passed"  value={`${report.passed} / ${report.totalQuestions}`} tone="success" icon={<CheckCircle2 size={22}/>}/>
            <StatCard label="Failed"         value={report.failed}                               tone="danger"  icon={<XCircle size={22}/>}/>
            <StatCard label="Critical Misses" value={report.topFailures.filter(f => f.weight >= 5).length} tone="danger" icon={<AlertTriangle size={22}/>} sub="High-weight failures"/>
          </div>

          {/* Verdict band */}
          <div style={{
            padding: 16, marginBottom: 18, borderRadius: 12,
            backgroundColor: readinessLabel.tone === 'success' ? '#ecfdf5' : readinessLabel.tone === 'warning' ? '#fefce8' : '#fef2f2',
            color: readinessLabel.tone === 'success' ? '#065f46' : readinessLabel.tone === 'warning' ? '#854d0e' : '#991b1b',
            border: `1px solid ${readinessLabel.tone === 'success' ? '#a7f3d0' : readinessLabel.tone === 'warning' ? '#fde68a' : '#fecaca'}`,
          }}>
            <strong style={{ fontSize: '1rem' }}>{readinessLabel.label}</strong>
            <div style={{ fontSize: '0.86rem', marginTop: 4 }}>{readinessLabel.desc}</div>
          </div>

          {/* Chapter breakdown */}
          <SectionCard variant="white" title="Chapter Breakdown">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {report.chapters.map(c => {
                const tone = c.score >= 85 ? theme.success : c.score >= 65 ? theme.warning : theme.danger;
                return (
                  <div key={c.chapter} style={{ padding: 12, backgroundColor: theme.bg, borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <strong style={{ color: theme.navy }}>{c.chapter}</strong>
                      <strong style={{ color: tone }}>{c.score}%</strong>
                    </div>
                    <div style={{ height: 6, backgroundColor: '#e5e9f0', borderRadius: 3 }}>
                      <div style={{ height: '100%', width: `${c.score}%`, backgroundColor: tone, borderRadius: 3 }}/>
                    </div>
                    <div style={{ fontSize: '0.74rem', color: theme.textMuted, marginTop: 4 }}>{c.passed}/{c.total} checks passed</div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Top failures */}
          {report.topFailures.length > 0 && (
            <SectionCard variant="cream" bordered title="🚨 Top Failures — Fix These First">
              {report.topFailures.map(f => (
                <div key={f.id} style={{ padding: 12, marginBottom: 8, backgroundColor: theme.surface, borderRadius: 8, borderLeft: `4px solid ${theme.danger}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <code style={{ fontSize: '0.78rem', color: theme.danger, fontWeight: 700 }}>{f.id}</code>
                    <StatusPill preset="danger" label="FAIL" size="sm"/>
                    <span style={{ fontSize: '0.74rem', color: theme.textMuted }}>weight {f.weight}</span>
                  </div>
                  <div style={{ fontWeight: 600, color: theme.navy, fontSize: '0.92rem' }}>{f.question}</div>
                  <div style={{ fontSize: '0.82rem', color: theme.textMuted, marginTop: 4 }}>{f.detail}</div>
                </div>
              ))}
            </SectionCard>
          )}

          {/* Full results */}
          <SectionCard variant="white" title="All Checks">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Result</th>
                  <th style={th}>Code</th>
                  <th style={th}>Question</th>
                  <th style={th}>Detail</th>
                  <th style={th}>Weight</th>
                </tr>
              </thead>
              <tbody>
                {report.results.map(r => (
                  <tr key={r.id} style={r.pass ? {} : { backgroundColor: '#fff7ed' }}>
                    <td style={td}>{r.pass ? <span style={{ color: theme.success }}>✓</span> : <span style={{ color: theme.danger }}>✗</span>}</td>
                    <td style={td}><code style={{ fontSize: '0.78rem', fontWeight: 700, color: theme.primary }}>{r.id}</code></td>
                    <td style={td}>{r.question}</td>
                    <td style={td}><span style={{ color: theme.textMuted, fontSize: '0.82rem' }}>{r.detail}</span></td>
                    <td style={td}>{r.weight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>

          <div style={{ marginTop: 14, color: theme.textMuted, fontSize: '0.78rem' }}>
            Last run: {new Date(report.runAt).toLocaleString('en-IN')}
          </div>
        </>
      )}
    </div>
  );
}

const primaryBtn = { padding: '9px 16px', backgroundColor: theme.primary, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 };
const td = { padding: '10px 14px', borderBottom: `1px solid ${theme.borderLight}`, fontSize: '0.86rem', color: theme.navy, verticalAlign: 'top' };
const th = { padding: '11px 14px', borderBottom: `1px solid ${theme.border}`, color: theme.textMuted, textAlign: 'left', fontSize: '0.74rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', backgroundColor: '#fafbfc' };
